import { useState, useContext } from 'react';
import { format } from 'date-fns';
import csLocale from 'date-fns/locale/cs';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import AuthContext from '../contexts/AuthContext';
import { useAxios } from '../hooks/useAxios';
import { getIsoDate } from '../utils/dateHandler';
import {
  hasAnyApplication,
  hasPendingApplication,
  isAssignedAvailability,
} from '../utils/employeeCalendarUtils';
import {
  formatTimeInputValue,
  mobileTimeFieldSx,
  parseTimeInputValue,
} from './mobileTimeFieldConfig';

const dialogPaperProps = {
  className: 'glass-surface-accent glass-modal',
  sx: { borderRadius: '16px' },
};
const dialogBackdropProps = {
  sx: { backgroundColor: 'rgba(6, 6, 8, 0.65)', backdropFilter: 'blur(8px)' },
};

const renderAllocationMeta = (allocation, type) => (
  <>
    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '1rem' }}>
      {allocation.name}
    </Typography>
    <Typography variant="body2" sx={{ display: 'block', mt: 0.25, color: 'var(--ink-2)' }}>
      {format(getIsoDate(allocation.startTime), 'HH:mm')} –{' '}
      {format(getIsoDate(allocation.endTime), 'HH:mm')}
      {allocation.outlet?.name && <> · {allocation.outlet.name}</>}
      {allocation.shiftScope?.name && <> · {allocation.shiftScope.name}</>}
    </Typography>
    {type === 'offered' && allocation.employee?.fullNameReversed && (
      <Typography variant="body2" sx={{ display: 'block', mt: 0.25, color: 'var(--ink-3)' }}>
        Nabídl: {allocation.employee.fullNameReversed}
      </Typography>
    )}
  </>
);

/** Stable module-level component — must not be recreated inside the hook. */
const EmployeeShiftDialogsView = ({
  dialogOpen,
  closeAvailabilityDialog,
  selectedAvailability,
  assignedAvailability,
  assignedShift,
  dialogDay,
  startTime,
  setStartTime,
  endTime,
  setEndTime,
  openDeleteConfirm,
  canRequestActions,
  openReasonDialog,
  handleSaveAvailability,
  isBusy,
  isDeleteConfirmOpen,
  closeDeleteConfirm,
  handleDeleteAvailability,
  reasonDialogOpen,
  closeReasonDialog,
  reasonDialogType,
  reasonText,
  setReasonText,
  submitReason,
  allocationDialogOpen,
  closeAllocationDialog,
  selectedAllocation,
  allocationDialogType,
  employeeId,
  handleWithdrawApplication,
  handleApplyAllocation,
}) => (
  <>
    <Dialog
      open={dialogOpen}
      onClose={isBusy ? undefined : closeAvailabilityDialog}
      fullWidth
      maxWidth="sm"
      PaperProps={dialogPaperProps}
      BackdropProps={dialogBackdropProps}
    >
      <DialogTitle sx={{ color: 'var(--ink)' }}>
        {selectedAvailability && assignedAvailability ? 'Detail směny' : 'Dostupnost'}
        <Typography variant="body2" sx={{ color: 'var(--ink-2)' }}>
          {dialogDay && format(dialogDay, 'd. MMMM yyyy', { locale: csLocale })}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {selectedAvailability?.isCancellationRequested && (
          <Typography variant="body2" sx={{ mb: 1.5, color: 'var(--ink-2)' }}>
            Žádost o zrušení odeslána
            {selectedAvailability.cancellationReason
              ? `: ${selectedAvailability.cancellationReason}`
              : ''}
          </Typography>
        )}
        {selectedAvailability?.isOfferRequested && (
          <Typography variant="body2" sx={{ mb: 1.5, color: 'var(--ink-2)' }}>
            Směna nabídnuta
            {selectedAvailability.offerReason ? `: ${selectedAvailability.offerReason}` : ''}
          </Typography>
        )}

        {assignedAvailability && assignedShift && (
          <Box className="glass-shift-fill glass-list-card" sx={{ mb: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 700, color: '#fff' }}>
              {assignedShift.name}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
              {format(getIsoDate(assignedShift.startTime), 'HH:mm')} –{' '}
              {format(getIsoDate(assignedShift.endTime), 'HH:mm')}
              {assignedShift.outlet?.name && <> · {assignedShift.outlet.name}</>}
              {assignedShift.shiftScope?.name && <> · {assignedShift.shiftScope.name}</>}
            </Typography>
          </Box>
        )}

        {!assignedAvailability && (
          <>
            <TextField
              label="Od"
              type="time"
              value={formatTimeInputValue(startTime)}
              onChange={(event) => {
                const next = parseTimeInputValue(dialogDay, event.target.value);
                if (next) setStartTime(next);
              }}
              fullWidth
              disabled={isBusy}
              InputLabelProps={{ shrink: true }}
              sx={{ mt: 2, mb: 2, ...mobileTimeFieldSx }}
            />
            <TextField
              label="Do"
              type="time"
              value={formatTimeInputValue(endTime)}
              onChange={(event) => {
                const next = parseTimeInputValue(dialogDay, event.target.value);
                if (next) setEndTime(next);
              }}
              fullWidth
              disabled={isBusy}
              InputLabelProps={{ shrink: true }}
              sx={mobileTimeFieldSx}
            />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 0.5 }}>
        {selectedAvailability && !assignedAvailability && (
          <Button
            onClick={openDeleteConfirm}
            color="error"
            startIcon={<Delete />}
            disabled={isBusy}
          >
            Smazat
          </Button>
        )}
        {canRequestActions && (
          <>
            <Button variant="outlined" onClick={() => openReasonDialog('cancel')} disabled={isBusy}>
              Zrušit směnu
            </Button>
            <Button variant="contained" onClick={() => openReasonDialog('offer')} disabled={isBusy}>
              Nabídnout
            </Button>
          </>
        )}
        <Button onClick={closeAvailabilityDialog} sx={{ color: 'var(--ink-2)' }} disabled={isBusy}>
          Zavřít
        </Button>
        {!assignedAvailability && (
          <Button
            onClick={handleSaveAvailability}
            variant="contained"
            className="glass-accent-fill"
            disabled={isBusy}
          >
            Uložit
          </Button>
        )}
      </DialogActions>
    </Dialog>

    <Dialog
      open={isDeleteConfirmOpen}
      onClose={isBusy ? undefined : closeDeleteConfirm}
      fullWidth
      maxWidth="xs"
      PaperProps={dialogPaperProps}
      BackdropProps={dialogBackdropProps}
    >
      <DialogTitle sx={{ color: 'var(--ink)' }}>Smazat?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: 'var(--ink-2)' }}>
          Opravdu chcete smazat tento čas? Tuto akci nelze vrátit zpět.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDeleteConfirm} sx={{ color: 'var(--ink-2)' }} disabled={isBusy}>
          Zavřít
        </Button>
        <Button
          variant="contained"
          color="error"
          disabled={isBusy}
          onClick={() => handleDeleteAvailability(selectedAvailability._id)}
        >
          Smazat
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog
      open={reasonDialogOpen}
      onClose={isBusy ? undefined : closeReasonDialog}
      fullWidth
      maxWidth="sm"
      PaperProps={dialogPaperProps}
      BackdropProps={dialogBackdropProps}
    >
      <DialogTitle sx={{ color: 'var(--ink)' }}>
        {reasonDialogType === 'offer' ? 'Nabídnout směnu' : 'Zrušit směnu'}
      </DialogTitle>
      <DialogContent>
        <TextField
          label="Důvod"
          value={reasonText}
          onChange={(e) => setReasonText(e.target.value)}
          multiline
          minRows={2}
          fullWidth
          disabled={isBusy}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={closeReasonDialog} disabled={isBusy}>
          Zpět
        </Button>
        <Button variant="contained" disabled={isBusy || !reasonText.trim()} onClick={submitReason}>
          Odeslat
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog
      open={allocationDialogOpen}
      onClose={isBusy ? undefined : closeAllocationDialog}
      fullWidth
      maxWidth="sm"
      PaperProps={dialogPaperProps}
      BackdropProps={dialogBackdropProps}
    >
      <DialogTitle sx={{ color: 'var(--ink)' }}>
        {allocationDialogType === 'open' ? 'Volná směna' : 'Nabízená směna'}
      </DialogTitle>
      <DialogContent>
        {selectedAllocation && renderAllocationMeta(selectedAllocation, allocationDialogType)}
        {selectedAllocation && hasPendingApplication(selectedAllocation, employeeId) && (
          <Typography variant="body2" sx={{ mt: 2, color: 'var(--ink-2)' }}>
            Čeká na schválení.
          </Typography>
        )}
        {selectedAllocation &&
          hasAnyApplication(selectedAllocation, employeeId) &&
          !hasPendingApplication(selectedAllocation, employeeId) && (
            <Typography variant="body2" sx={{ mt: 2, color: 'var(--ink-2)' }}>
              Vaše žádost byla zpracována.
            </Typography>
          )}
      </DialogContent>
      <DialogActions>
        <Button onClick={closeAllocationDialog} disabled={isBusy}>
          Zavřít
        </Button>
        {selectedAllocation && hasPendingApplication(selectedAllocation, employeeId) && (
          <Button
            variant="outlined"
            color="error"
            disabled={isBusy}
            onClick={handleWithdrawApplication}
          >
            Stáhnout žádost
          </Button>
        )}
        {selectedAllocation && !hasAnyApplication(selectedAllocation, employeeId) && (
          <Button
            variant="contained"
            className="glass-accent-fill"
            disabled={isBusy}
            onClick={handleApplyAllocation}
          >
            Vzít směnu
          </Button>
        )}
      </DialogActions>
    </Dialog>
  </>
);

export const useEmployeeShiftDialogs = (onRefresh) => {
  const { token, employeeId } = useContext(AuthContext);
  const { sendRequest } = useAxios();

  const [dialogDay, setDialogDay] = useState(null);
  const [selectedAvailability, setSelectedAvailability] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);

  const [selectedAllocation, setSelectedAllocation] = useState(null);
  const [allocationDialogType, setAllocationDialogType] = useState(null);
  const [allocationDialogOpen, setAllocationDialogOpen] = useState(false);

  const [reasonDialogType, setReasonDialogType] = useState(null);
  const [reasonText, setReasonText] = useState('');
  const [reasonDialogOpen, setReasonDialogOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isBusy, setIsBusy] = useState(false);

  const openAvailabilityDialog = (day, availability = null) => {
    if (isBusy) return;
    setDialogDay(day);
    setSelectedAvailability(availability);
    if (availability) {
      setStartTime(new Date(availability.startTime));
      setEndTime(new Date(availability.endTime));
    } else {
      const defaultStart = new Date(day);
      defaultStart.setHours(9, 0, 0, 0);
      const defaultEnd = new Date(day);
      defaultEnd.setHours(17, 0, 0, 0);
      setStartTime(defaultStart);
      setEndTime(defaultEnd);
    }
    setDialogOpen(true);
  };

  const closeAvailabilityDialog = () => {
    if (isBusy) return;
    setDialogOpen(false);
    setDialogDay(null);
    setSelectedAvailability(null);
    setStartTime(null);
    setEndTime(null);
    setIsDeleteConfirmOpen(false);
  };

  const openAllocationDialog = (allocation, type) => {
    if (isBusy) return;
    setSelectedAllocation(allocation);
    setAllocationDialogType(type);
    setAllocationDialogOpen(true);
  };

  const closeAllocationDialog = () => {
    if (isBusy) return;
    setAllocationDialogOpen(false);
    setSelectedAllocation(null);
    setAllocationDialogType(null);
  };

  const handleSaveAvailability = async () => {
    if (isBusy || !dialogDay || !startTime || !endTime) return;

    const startDateTime = new Date(dialogDay);
    startDateTime.setHours(startTime.getHours(), startTime.getMinutes(), 0, 0);
    const endDateTime = new Date(dialogDay);
    endDateTime.setHours(endTime.getHours(), endTime.getMinutes(), 0, 0);

    setIsBusy(true);
    try {
      if (selectedAvailability) {
        await sendRequest(
          `/employeeAvailabilities/${selectedAvailability._id}/update`,
          'PATCH',
          {
            startTime: startDateTime.toISOString(),
            endTime: endDateTime.toISOString(),
          },
          { Authorization: `Bearer ${token}` }
        );
      } else {
        await sendRequest(
          '/employeeAvailabilities/create',
          'POST',
          {
            date: format(dialogDay, 'yyyy-MM-dd'),
            startTime: startDateTime.toISOString(),
            endTime: endDateTime.toISOString(),
          },
          { Authorization: `Bearer ${token}` }
        );
      }
      setDialogOpen(false);
      setDialogDay(null);
      setSelectedAvailability(null);
      setStartTime(null);
      setEndTime(null);
      setIsDeleteConfirmOpen(false);
      await onRefresh();
    } finally {
      setIsBusy(false);
    }
  };

  const handleDeleteAvailability = async (availabilityId) => {
    if (isBusy || !availabilityId) return;
    setIsBusy(true);
    try {
      await sendRequest(`/employeeAvailabilities/${availabilityId}/delete`, 'DELETE', null, {
        Authorization: `Bearer ${token}`,
      });
      setIsDeleteConfirmOpen(false);
      setDialogOpen(false);
      setDialogDay(null);
      setSelectedAvailability(null);
      setStartTime(null);
      setEndTime(null);
      await onRefresh();
    } finally {
      setIsBusy(false);
    }
  };

  const openDeleteConfirm = () => {
    if (isBusy || !selectedAvailability?._id) return;
    setIsDeleteConfirmOpen(true);
  };

  const closeDeleteConfirm = () => {
    if (isBusy) return;
    setIsDeleteConfirmOpen(false);
  };

  const openReasonDialog = (type) => {
    if (isBusy) return;
    setReasonDialogType(type);
    setReasonText('');
    setReasonDialogOpen(true);
  };

  const closeReasonDialog = () => {
    if (isBusy) return;
    setReasonDialogOpen(false);
    setReasonDialogType(null);
    setReasonText('');
  };

  const submitReason = async () => {
    if (isBusy || !selectedAvailability || !reasonText.trim()) return;

    const endpoint =
      reasonDialogType === 'offer'
        ? `/employeeAvailabilities/${selectedAvailability._id}/offer`
        : `/employeeAvailabilities/${selectedAvailability._id}/cancel`;

    const body =
      reasonDialogType === 'offer'
        ? { offerReason: reasonText.trim() }
        : { cancellationReason: reasonText.trim() };

    setIsBusy(true);
    try {
      await sendRequest(endpoint, 'PATCH', body, { Authorization: `Bearer ${token}` });
      setReasonDialogOpen(false);
      setReasonDialogType(null);
      setReasonText('');
      setDialogOpen(false);
      setDialogDay(null);
      setSelectedAvailability(null);
      setStartTime(null);
      setEndTime(null);
      setIsDeleteConfirmOpen(false);
      await onRefresh();
    } finally {
      setIsBusy(false);
    }
  };

  const handleApplyAllocation = async () => {
    if (isBusy || !selectedAllocation) return;
    const endpoint =
      allocationDialogType === 'open'
        ? `/employeeAvailabilities/applyOpen/${selectedAllocation._id}`
        : `/employeeAvailabilities/apply/${selectedAllocation._id}`;

    setIsBusy(true);
    try {
      await sendRequest(endpoint, 'POST', null, { Authorization: `Bearer ${token}` });
      setAllocationDialogOpen(false);
      setSelectedAllocation(null);
      setAllocationDialogType(null);
      await onRefresh();
    } finally {
      setIsBusy(false);
    }
  };

  const handleWithdrawApplication = async () => {
    if (isBusy || !selectedAllocation) return;
    setIsBusy(true);
    try {
      await sendRequest(
        `/shiftAllocations/${selectedAllocation._id}/applications/withdraw`,
        'DELETE',
        null,
        { Authorization: `Bearer ${token}` }
      );
      setAllocationDialogOpen(false);
      setSelectedAllocation(null);
      setAllocationDialogType(null);
      await onRefresh();
    } finally {
      setIsBusy(false);
    }
  };

  const assignedAvailability = isAssignedAvailability(selectedAvailability);
  const assignedShift = selectedAvailability?.shiftAllocationId;
  const canRequestActions =
    assignedAvailability &&
    !selectedAvailability?.isCancellationRequested &&
    !selectedAvailability?.isOfferRequested;

  return {
    openAvailabilityDialog,
    openAllocationDialog,
    // JSX element (not a component factory) so React keeps dialog identity across refreshes
    dialogs: (
      <EmployeeShiftDialogsView
        dialogOpen={dialogOpen}
        closeAvailabilityDialog={closeAvailabilityDialog}
        selectedAvailability={selectedAvailability}
        assignedAvailability={assignedAvailability}
        assignedShift={assignedShift}
        dialogDay={dialogDay}
        startTime={startTime}
        setStartTime={setStartTime}
        endTime={endTime}
        setEndTime={setEndTime}
        openDeleteConfirm={openDeleteConfirm}
        canRequestActions={canRequestActions}
        openReasonDialog={openReasonDialog}
        handleSaveAvailability={handleSaveAvailability}
        isBusy={isBusy}
        isDeleteConfirmOpen={isDeleteConfirmOpen}
        closeDeleteConfirm={closeDeleteConfirm}
        handleDeleteAvailability={handleDeleteAvailability}
        reasonDialogOpen={reasonDialogOpen}
        closeReasonDialog={closeReasonDialog}
        reasonDialogType={reasonDialogType}
        reasonText={reasonText}
        setReasonText={setReasonText}
        submitReason={submitReason}
        allocationDialogOpen={allocationDialogOpen}
        closeAllocationDialog={closeAllocationDialog}
        selectedAllocation={selectedAllocation}
        allocationDialogType={allocationDialogType}
        employeeId={employeeId}
        handleWithdrawApplication={handleWithdrawApplication}
        handleApplyAllocation={handleApplyAllocation}
      />
    ),
  };
};
